import {
  FormControl,
  MenuItem,
  menuItemClasses,
  Select,
  selectClasses,
  styled,
  Typography,
} from '@mui/material';
import IconArrowDown from 'assets/img/icons/redesign/arrow-down.svg?react';
import { StyledFormHelperText } from 'public/components/common/forms/form-helper-text/form-helper-text';
import { StyledFormInputLabel } from 'public/components/common/forms/form-input-label/form-input-label';
import { CertificateSelectProps } from './types';

export const CertificateSelect = ({
  error,
  placeholder = '',
  onChange,
  items,
  selectedValue,
  label,
  required,
  defaultLabel,
  defaultItemValue,
}: CertificateSelectProps) => {
  return (
    <FormControl variant="filled" fullWidth>
      <StyledFormInputLabel required={required} id={`${label}-label`}>
        {label}
      </StyledFormInputLabel>
      <StyledSelect
        value={selectedValue ?? ''}
        onChange={(e) => onChange(e.target.value as string)}
        MenuProps={{
          PaperProps: {
            sx: (theme) => ({
              borderRadius: theme.spacing(1),
            }),
          },
        }}
        IconComponent={IconArrowDown}
        displayEmpty
        renderValue={(selected) => {
          if (!selected || selected === '') {
            return <StyledPlaceholder>{placeholder}</StyledPlaceholder>;
          }

          const item = items.find((i) => i.value === selected);

          return <StyledSelectValue>{item ? item.label : selected}</StyledSelectValue>;
        }}
      >
        {defaultLabel ? (
          <StyledMenuItem key="placeholder" value={defaultItemValue ?? ''}>
            <StyledPlaceholder>{defaultLabel}</StyledPlaceholder>
          </StyledMenuItem>
        ) : null}
        {items.map((item) => (
          <StyledMenuItem key={item.value} value={item.value}>
            {item.label}
          </StyledMenuItem>
        ))}
      </StyledSelect>
      {error ? <StyledFormHelperText>{error}</StyledFormHelperText> : null}
    </FormControl>
  );
};

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.lwPrimaryAccentLight[5],
  },

  [`&.${menuItemClasses.focusVisible}`]: {
    backgroundColor: theme.palette.lwPrimaryAccentDark[10],
  },

  [`&.${menuItemClasses.selected}`]: {
    backgroundColor: 'transparent',
  },

  [`&.${menuItemClasses.selected}:hover`]: {
    backgroundColor: theme.palette.lwPrimaryAccentLight[5],
  },
}));

export const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.lwWhite[100],
  marginBottom: theme.spacing(2),

  [`& .${selectClasses.icon}`]: {
    right: theme.spacing(6),
    top: '50%',
    transform: 'translateY(-50%)',
    width: theme.spacing(6),
    height: theme.spacing(6),
    flexShrink: 0,
  },

  [`& .${selectClasses.select}`]: {
    height: theme.spacing(12),
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(4),
    fontSize: theme.typography.pxToRem(14),
    fontVariationSettings: '"wght" 500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  [`&&& .${selectClasses.select}`]: {
    paddingRight: theme.spacing(12),
  },
}));

const StyledSelectValue = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const StyledPlaceholder = styled(Typography)(({ theme }) => ({
  color: theme.palette.lwPrimaryAccentDark[40],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  height: theme.spacing(5),
}));
